// Suchfeld mit Autovervollständigung (Combobox nach WAI-ARIA: aria-activedescendant, Pfeiltasten/Enter/Tab/Escape)
// und Filter-Chips. Die Suche startet erst beim Absenden (Enter/Knopf).
import type { UserDto } from '@lagerfeuer/shared';
import { useQuery } from '@tanstack/react-query';
import { useId, useLayoutEffect, useMemo, useRef, useState, type KeyboardEvent } from 'react';
import { lookupApi } from '../api';
import { Button } from '../ui';
import { channelSuggestions, chipLabel, staticSuggestions, useDebouncedValue, userSuggestions, type Suggestion } from './helpers';
import { activeTokenAt, parseQuery, removeToken, replaceToken } from './query-parser';
import './search.css';

const USER_LOOKUP_MIN = 2;

export function SearchBox(props: {
  value: string;
  onChange: (value: string) => void;
  /** Wird nur bei fehlerfreier Eingabe aufgerufen. */
  onSubmit: (value: string) => void;
  /** Server-Kontext für `in:`-Vorschläge. */
  serverId?: string | null;
  /** Zuletzt gesehene Autoren (Vorschläge für from:/mentions: vor dem Lookup). */
  recentAuthors?: UserDto[];
  autoFocus?: boolean;
  busy?: boolean;
}) {
  const { value, onChange } = props;
  const inputRef = useRef<HTMLInputElement>(null);
  const pendingCursor = useRef<number | null>(null);
  const listId = useId();
  const errorId = useId();
  const [cursor, setCursor] = useState(value.length);
  const [open, setOpen] = useState(false);
  const [focused, setFocused] = useState(false);
  const [activeIndex, setActiveIndex] = useState(-1);
  const [showAllErrors, setShowAllErrors] = useState(false);

  const parsed = useMemo(() => parseQuery(value), [value]);
  const active = useMemo(() => activeTokenAt(value, Math.min(cursor, value.length)), [value, cursor]);

  const userKey = active.kind === 'value' && (active.key === 'from' || active.key === 'mentions') ? active.key : null;
  const userPrefix = userKey ? active.prefix : '';
  const debouncedPrefix = useDebouncedValue(userPrefix, 250);
  const lookupPrefix = userKey && debouncedPrefix.length >= USER_LOOKUP_MIN && userPrefix.length >= USER_LOOKUP_MIN ? debouncedPrefix : '';
  const users = useQuery({
    queryKey: ['extras', 'search', 'users', lookupPrefix],
    queryFn: () => lookupApi.searchUsers(lookupPrefix, 8),
    enabled: open && !!lookupPrefix,
    staleTime: 30_000,
  });

  const wantsChannels = active.kind === 'value' && active.key === 'in';
  const channels = useQuery({
    queryKey: ['extras', 'search', 'channels', props.serverId],
    queryFn: () => lookupApi.channels(props.serverId as string),
    enabled: open && wantsChannels && !!props.serverId,
    staleTime: 60_000,
  });

  const { suggestions, hint } = useMemo((): { suggestions: Suggestion[]; hint: string | null } => {
    if (active.kind === 'value' && (active.key === 'from' || active.key === 'mentions')) {
      if (active.prefix.length < USER_LOOKUP_MIN) {
        const recent = userSuggestions(active.key, props.recentAuthors ?? [], active.prefix);
        return { suggestions: recent, hint: recent.length ? null : 'Mindestens 2 Zeichen eingeben, um Personen zu suchen.' };
      }
      if (users.data && lookupPrefix) {
        const list = userSuggestions(active.key, users.data, '');
        return { suggestions: list, hint: list.length ? null : 'Keine Person gefunden.' };
      }
      return { suggestions: [], hint: users.isError ? 'Personensuche fehlgeschlagen.' : 'Suche Personen …' };
    }
    if (active.kind === 'value' && active.key === 'in') {
      if (!props.serverId) return { suggestions: [], hint: 'Kanalvorschläge gibt es nur innerhalb eines Servers. Kanalname oder #ID direkt eingeben.' };
      if (channels.data) {
        const list = channelSuggestions(channels.data, active.prefix);
        return { suggestions: list, hint: list.length ? null : 'Kein passender Kanal.' };
      }
      return { suggestions: [], hint: channels.isError ? 'Kanäle konnten nicht geladen werden.' : 'Lädt Kanäle …' };
    }
    const list = staticSuggestions(active);
    const dateHint = active.kind === 'value' && (active.key === 'before' || active.key === 'after' || active.key === 'during') ? 'Format: JJJJ-MM-TT, z. B. 2026-10-05' : null;
    return { suggestions: list, hint: dateHint };
  }, [active, props.recentAuthors, props.serverId, users.data, users.isError, lookupPrefix, channels.data, channels.isError]);

  const popupVisible = open && focused && (suggestions.length > 0 || !!hint);
  const listVisible = popupVisible && suggestions.length > 0;
  const current = activeIndex >= 0 && activeIndex < suggestions.length ? activeIndex : -1;
  const optionId = (i: number) => `${listId}-opt-${i}`;

  useLayoutEffect(() => {
    if (pendingCursor.current === null || !inputRef.current) return;
    inputRef.current.setSelectionRange(pendingCursor.current, pendingCursor.current);
    pendingCursor.current = null;
  }, [value]);

  const visibleErrors = showAllErrors ? parsed.errors : parsed.errors.filter((e) => !(focused && cursor > e.token.start && cursor <= e.token.end));

  function apply(s: Suggestion) {
    const next = replaceToken(value, active, s.insert);
    pendingCursor.current = next.cursor;
    setCursor(next.cursor);
    setActiveIndex(-1);
    setOpen(true);
    onChange(next.input);
    inputRef.current?.focus();
  }

  function submit() {
    if (parsed.errors.length) {
      setShowAllErrors(true);
      return;
    }
    setShowAllErrors(false);
    setOpen(false);
    props.onSubmit(value);
  }

  function onKeyDown(e: KeyboardEvent<HTMLInputElement>) {
    if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
      if (!listVisible) {
        if (e.key === 'ArrowDown') {
          setOpen(true);
          e.preventDefault();
        }
        return;
      }
      e.preventDefault();
      const n = suggestions.length;
      setActiveIndex(e.key === 'ArrowDown' ? (current + 1) % n : current <= 0 ? n - 1 : current - 1);
    } else if (e.key === 'Enter') {
      if (listVisible && current >= 0) {
        e.preventDefault();
        apply(suggestions[current]);
      }
    } else if (e.key === 'Tab' && !e.shiftKey) {
      if (listVisible) {
        e.preventDefault();
        apply(suggestions[current >= 0 ? current : 0]);
      }
    } else if (e.key === 'Escape') {
      if (popupVisible) {
        e.preventDefault();
        e.stopPropagation();
        setOpen(false);
        setActiveIndex(-1);
      }
    }
  }

  const chips = parsed.tokens.filter((t) => t.kind !== 'word');

  return (
    <form
      role="search"
      className="lfx-search-box"
      data-testid="search-form"
      onSubmit={(e) => {
        e.preventDefault();
        submit();
      }}
    >
      <div className="lfx-search-box__row">
        <div className="lfx-search-box__field">
          <label className="lfx-search-sr-only" htmlFor={`${listId}-input`}>
            Nachrichten suchen
          </label>
          <input
            ref={inputRef}
            id={`${listId}-input`}
            className="lfx-input lfx-search-box__input"
            type="text"
            role="combobox"
            autoComplete="off"
            spellCheck={false}
            autoFocus={props.autoFocus}
            placeholder="Suchen … z. B. from:anna has:bild"
            aria-autocomplete="list"
            aria-expanded={listVisible}
            aria-controls={listId}
            aria-activedescendant={listVisible && current >= 0 ? optionId(current) : undefined}
            aria-invalid={visibleErrors.length > 0 || undefined}
            aria-describedby={visibleErrors.length ? errorId : undefined}
            data-testid="search-input"
            value={value}
            onChange={(e) => {
              onChange(e.target.value);
              setCursor(e.target.selectionStart ?? e.target.value.length);
              setActiveIndex(-1);
              setOpen(true);
            }}
            onSelect={(e) => setCursor(e.currentTarget.selectionStart ?? e.currentTarget.value.length)}
            onFocus={() => {
              setFocused(true);
              setOpen(true);
            }}
            onBlur={() => {
              setFocused(false);
              setActiveIndex(-1);
            }}
            onKeyDown={onKeyDown}
          />
          {popupVisible && (
            <div className="lfx-search-popup" data-testid="search-suggestions">
              <ul id={listId} role="listbox" aria-label="Vorschläge" className="lfx-search-popup__list">
                {suggestions.map((s, i) => (
                  <li
                    key={s.id}
                    id={optionId(i)}
                    role="option"
                    aria-selected={i === current}
                    className={`lfx-search-option${i === current ? ' lfx-search-option--active' : ''}`}
                    data-testid="search-suggestion"
                    data-insert={s.insert}
                    onMouseDown={(e) => e.preventDefault()}
                    onMouseEnter={() => setActiveIndex(i)}
                    onClick={() => apply(s)}
                  >
                    <span className="lfx-search-option__label">{s.label}</span>
                    {s.detail && <span className="lfx-search-option__detail">{s.detail}</span>}
                  </li>
                ))}
              </ul>
              {hint && (
                <div className="lfx-search-popup__hint" role="status" data-testid="search-suggestion-hint">
                  {hint}
                </div>
              )}
            </div>
          )}
        </div>
        <Button type="submit" variant="primary" testId="search-submit" disabled={props.busy}>
          Suchen
        </Button>
      </div>

      {chips.length > 0 && (
        <ul className="lfx-search-chips" aria-label="Erkannte Filter" data-testid="search-chips">
          {chips.map((t) => {
            const invalid = parsed.errors.some((e) => e.token === t);
            return (
              <li
                key={`${t.start}-${t.raw}`}
                className={`lfx-search-chip lfx-search-chip--${t.kind}${invalid ? ' lfx-search-chip--invalid' : ''}`}
                data-testid="search-chip"
                data-kind={t.kind}
                data-key={t.key}
                data-invalid={invalid ? 'true' : 'false'}
              >
                <span>{chipLabel(t)}</span>
                <button
                  type="button"
                  className="lfx-search-chip__remove"
                  aria-label={`Entfernen: ${chipLabel(t)}`}
                  data-testid="search-chip-remove"
                  onClick={() => {
                    const next = removeToken(value, t);
                    pendingCursor.current = next.length;
                    setCursor(next.length);
                    onChange(next);
                  }}
                >
                  ×
                </button>
              </li>
            );
          })}
        </ul>
      )}

      {visibleErrors.length > 0 && (
        <ul className="lfx-search-errors" id={errorId} role="alert" data-testid="search-parse-errors">
          {visibleErrors.map((e) => (
            <li key={`${e.token.start}-${e.message}`} data-testid="search-parse-error">
              <code className="lfx-mono">{e.token.raw}</code> {e.message}
            </li>
          ))}
        </ul>
      )}
    </form>
  );
}
